/**
 * LA CANCHA — la parte PURA (geometría y agregados por zona).
 *
 * `components/Court.tsx` dibuja; esto decide DÓNDE. Separado para poder
 * probarlo sin DOM (`test/court.test.ts`): una zona dibujada en el lugar
 * equivocado es el error que un entrenador detecta en dos segundos y que no
 * se perdona en una demo.
 *
 * ## Convención (la de toda planilla de vóley)
 *
 * Vista DESDE ATRÁS de la cancha propia, con la red arriba:
 *
 *     Z4  Z3  Z2      ← red (zona de ataque, 3 m)
 *     Z5  Z6  Z1      ← fondo (6 m); Z1 es la del saque
 *
 * Unidades: 10 por metro (la cancha mide 90 × 180). Las zonas de ataque miden
 * 3 × 3 m y las de defensa 3 × 6 m, como en el piso real; dibujarlas iguales
 * mentiría sobre dónde cae la pelota.
 *
 * `orientation`:
 *  - `'up'`   — la red está ARRIBA de esta media cancha (la que mira el
 *               espectador desde atrás);
 *  - `'down'` — la media cancha girada 180°: la red está ABAJO. Es la del
 *               rival en una cancha completa, o la propia cuando el DT está
 *               sentado del otro lado.
 */

/** Unidades por metro. */
export const U = 10;
/** Ancho de la cancha (9 m). */
export const COURT_W = 9 * U;
/** Largo de MEDIA cancha (9 m). */
export const HALF_L = 9 * U;
/** Profundidad de la zona de ataque (3 m). */
export const FRONT_DEPTH = 3 * U;
/** Zona libre a los costados. */
export const FREE_SIDE = 14;
/** Zona libre detrás de la línea de fondo (el sacador vive acá). */
export const FREE_BACK = 18;
/** Margen del lado de la red en una media cancha (para la red y las antenas). */
export const FREE_NET = 10;

export type HalfOrientation = 'up' | 'down';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

/** Columna (0 izquierda … 2 derecha) y fila (0 ataque, 1 defensa) de cada zona, vista `up`. */
const ZONE_GRID: Record<number, { col: number; row: number }> = {
  4: { col: 0, row: 0 },
  3: { col: 1, row: 0 },
  2: { col: 2, row: 0 },
  5: { col: 0, row: 1 },
  6: { col: 1, row: 1 },
  1: { col: 2, row: 1 },
};

/** Las seis zonas en orden de lectura (vista `up`): fila de ataque, después la de defensa. */
export const ZONES_READING_ORDER: readonly number[] = [4, 3, 2, 5, 6, 1];

export function isCourtZone(zone: number): boolean {
  return ZONE_GRID[zone] !== undefined;
}

/**
 * Rectángulo de una zona dentro de una media cancha cuyo borde izquierdo es
 * `x0` y cuyo borde SUPERIOR es `top` (sea la red o el fondo, según la
 * orientación).
 */
export function zoneRect(zone: number, orientation: HalfOrientation, x0: number, top: number): Rect {
  const cell = ZONE_GRID[zone];
  if (cell === undefined) throw new RangeError(`zona fuera de la cancha: ${zone}`);
  const w = COURT_W / 3;
  const h = cell.row === 0 ? FRONT_DEPTH : HALF_L - FRONT_DEPTH;
  const xUp = x0 + cell.col * w;
  const yUp = top + (cell.row === 0 ? 0 : FRONT_DEPTH);
  if (orientation === 'up') return { x: xUp, y: yUp, w, h };
  // 180°: se espeja en los dos ejes dentro de la media cancha.
  return { x: x0 + COURT_W - (xUp - x0) - w, y: top + HALF_L - (yUp - top) - h, w, h };
}

/**
 * Dónde se para la ficha de un jugador. No es el centro geométrico de la zona:
 * los delanteros se dibujan cerca de la red y los zagueros a media zona, que es
 * como se paran de verdad en la recepción y como lo dibuja el acta.
 */
export function playerSpot(position: number, orientation: HalfOrientation, x0: number, top: number): Point {
  const rect = zoneRect(position, orientation, x0, top);
  const cx = rect.x + rect.w / 2;
  const front = ZONE_GRID[position]?.row === 0;
  // Distancia desde la red, en unidades.
  const fromNet = front ? 13 : FRONT_DEPTH + 28;
  const y = orientation === 'up' ? top + fromNet : top + HALF_L - fromNet;
  return { x: cx, y };
}

export interface CourtLayout {
  /** Caja del SVG. */
  width: number;
  height: number;
  /** Borde izquierdo de la cancha. */
  x0: number;
  /** Medias canchas en orden de dibujo (de arriba hacia abajo). */
  halves: Array<{ orientation: HalfOrientation; top: number }>;
  /** Altura de la red (coordenada y). */
  netY: number;
}

/**
 * La caja completa.
 *
 * - **Media cancha** (`halves = 1`): sin girar, la red arriba; girada, abajo.
 * - **Cancha completa** (`halves = 2`): siempre dos mitades, la de arriba
 *   `down` y la de abajo `up`. Girar la cancha completa no cambia la geometría:
 *   cambia QUÉ equipo va abajo, y eso lo decide el componente.
 */
export function courtLayout(halves: 1 | 2, rotated = false): CourtLayout {
  const x0 = FREE_SIDE;
  const width = COURT_W + 2 * FREE_SIDE;
  if (halves === 2) {
    const top = FREE_BACK;
    return {
      width,
      height: 2 * HALF_L + 2 * FREE_BACK,
      x0,
      halves: [
        { orientation: 'down', top },
        { orientation: 'up', top: top + HALF_L },
      ],
      netY: top + HALF_L,
    };
  }
  if (!rotated) {
    return {
      width,
      height: HALF_L + FREE_NET + FREE_BACK,
      x0,
      halves: [{ orientation: 'up', top: FREE_NET }],
      netY: FREE_NET,
    };
  }
  return {
    width,
    height: HALF_L + FREE_NET + FREE_BACK,
    x0,
    halves: [{ orientation: 'down', top: FREE_BACK }],
    netY: FREE_BACK + HALF_L,
  };
}

// ---------------------------------------------------------------------------
// Agregados por zona (lo que se pinta en la "grilla de zonas")
// ---------------------------------------------------------------------------

export interface ZoneTally {
  zone: number;
  count: number;
  /** Proporción sobre el total de la muestra (incluye lo que no tiene zona). */
  share: number;
  /** Eficiencia ponderada de la zona (si la fuente la trae). */
  efficiency: number | null;
}

export interface ZoneDistribution {
  zones: ZoneTally[];
  /** Total de la muestra. */
  total: number;
  /** Lo que no cae en Z1..Z6 (combinaciones, zonas 7-9, sin codificar). */
  other: number;
}

/**
 * Destinos del armador (`SetterTargetLine[]`) → reparto por zona de salida.
 *
 * Sólo las claves `Z1`..`Z6` (zona de salida codificada) se pueden ubicar en la
 * cancha; una combinación (`X5`, `CF`) depende de la tabla de combinaciones de
 * cada equipo y una zona de llegada (`E3`) no dice desde dónde se armó. Esas
 * van a `other`, que se muestra al lado de la cancha: el total cierra siempre.
 */
export function setterZones(
  lines: ReadonlyArray<{ key: string; count: number; attackEfficiency: number }>,
): ZoneDistribution {
  const acc = new Map<number, { count: number; effSum: number }>();
  let total = 0;
  let other = 0;
  for (const line of lines) {
    total += line.count;
    const match = /^Z(\d)$/.exec(line.key);
    const zone = match ? Number(match[1]) : NaN;
    if (!isCourtZone(zone)) {
      other += line.count;
      continue;
    }
    const prev = acc.get(zone) ?? { count: 0, effSum: 0 };
    prev.count += line.count;
    prev.effSum += line.attackEfficiency * line.count;
    acc.set(zone, prev);
  }
  const zones: ZoneTally[] = [];
  for (const [zone, value] of acc) {
    zones.push({
      zone,
      count: value.count,
      share: total === 0 ? 0 : value.count / total,
      efficiency: value.count === 0 ? null : value.effSum / value.count,
    });
  }
  zones.sort((a, b) => a.zone - b.zone);
  return { zones, total, other };
}

/** Suma de varias listas de destinos por clave (p. ej. las dos fases del armador). */
export function mergeTargets<T extends { key: string; count: number; attackEfficiency: number }>(
  ...lists: ReadonlyArray<readonly T[]>
): Array<{ key: string; count: number; attackEfficiency: number }> {
  const acc = new Map<string, { count: number; effSum: number }>();
  for (const list of lists) {
    for (const line of list) {
      const prev = acc.get(line.key) ?? { count: 0, effSum: 0 };
      prev.count += line.count;
      prev.effSum += line.attackEfficiency * line.count;
      acc.set(line.key, prev);
    }
  }
  return [...acc].map(([key, v]) => ({
    key,
    count: v.count,
    attackEfficiency: v.count === 0 ? 0 : v.effSum / v.count,
  }));
}

export interface MatrixCellLike {
  startZone: number | null;
  endZone: number | null;
  total: number;
  kills: number;
  errors: number;
  /** Eficiencia de la celda, ya calculada con la convención activa. */
  efficiency: number;
}

export interface ZoneSummary {
  zone: number;
  total: number;
  kills: number;
  errors: number;
  /**
   * Eficiencia de la zona = promedio de las celdas PONDERADO por su total. Las
   * fórmulas de eficiencia son lineales en los conteos, así que esto da lo
   * mismo que recalcular con la suma — y respeta la convención elegida en
   * Ajustes sin tener que conocerla acá.
   */
  efficiency: number;
}

/**
 * La matriz salida × llegada proyectada sobre UN eje (`'start'` o `'end'`),
 * opcionalmente filtrada por la otra coordenada. Es lo que dibuja la pestaña
 * Zonas sobre la cancha: de dónde salió (filtrable) y adónde fue.
 */
export function matrixByZone(
  cells: readonly MatrixCellLike[],
  axis: 'start' | 'end',
  filter: { start?: number | null; end?: number | null } = {},
): { zones: ZoneSummary[]; total: number; other: number } {
  const acc = new Map<number, ZoneSummary & { effSum: number }>();
  let total = 0;
  let other = 0;
  for (const cell of cells) {
    if (filter.start !== undefined && filter.start !== null && cell.startZone !== filter.start) continue;
    if (filter.end !== undefined && filter.end !== null && cell.endZone !== filter.end) continue;
    total += cell.total;
    const zone = axis === 'start' ? cell.startZone : cell.endZone;
    if (zone === null || !isCourtZone(zone)) {
      other += cell.total;
      continue;
    }
    const prev = acc.get(zone) ?? { zone, total: 0, kills: 0, errors: 0, efficiency: 0, effSum: 0 };
    prev.total += cell.total;
    prev.kills += cell.kills;
    prev.errors += cell.errors;
    prev.effSum += cell.efficiency * cell.total;
    acc.set(zone, prev);
  }
  const zones = [...acc.values()]
    .map(({ effSum, ...z }) => ({ ...z, efficiency: z.total === 0 ? 0 : effSum / z.total }))
    .sort((a, b) => a.zone - b.zone);
  return { zones, total, other };
}

/**
 * Tinta legible sobre un paso de la rampa del heatmap (`--heat-0`…`--heat-5`):
 * los tres pasos claros llevan texto oscuro y los tres oscuros, blanco.
 */
export function inkForBucket(bucket: number): 'dark' | 'light' {
  return bucket >= 3 ? 'light' : 'dark';
}

/** Abreviatura de rol para la ficha (clave i18n `court.roles.*`). */
export const COURT_ROLES = ['setter', 'outside', 'opposite', 'middle', 'libero'] as const;
export type CourtRole = (typeof COURT_ROLES)[number];

export function courtRole(role: string | undefined): CourtRole | null {
  return role !== undefined && (COURT_ROLES as readonly string[]).includes(role) ? (role as CourtRole) : null;
}
